/**
 * crm.service.ts
 *
 * Typed API call wrappers for the CRM domain.
 * Uses the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type {
  StagesResponse,
  PlansResponse,
  LeadsResponse,
  CRMPlansParams,
  CRMLeadsParams,
  PipelineStageMapResponse,
  CRMDashboardChartsResponse,
  CRMLeadPerformanceResponse,
} from '@/types/crm.types';

export function getCRMStages(params?: { filtersArray?: string }): Promise<StagesResponse> {
  return apiClient
    .get<ApiResponse<StagesResponse>>('/crm/stages', {
      params: params?.filtersArray ? { filtersArray: params.filtersArray } : undefined,
    })
    .then((r) => r.data.data);
}

export function getCRMPlans(params: CRMPlansParams): Promise<PlansResponse> {
  return apiClient
    .post<ApiResponse<PlansResponse>>('/crm/plans', {
      stageID: params.stageID,
      legacyStageID: params.legacyStageID ?? '0',
      displayStart: String(params.displayStart),
      displayLength: String(params.displayLength),
      ...(params.filtersArray ? { filtersArray: params.filtersArray } : {}),
    })
    .then((r) => r.data.data);
}

export function getCRMLeads(params: CRMLeadsParams): Promise<LeadsResponse> {
  return apiClient
    .post<ApiResponse<LeadsResponse>>('/crm/leads', {
      displayStart: String(params.displayStart),
      displayLength: String(params.displayLength),
      ...(params.filtersArray ? { filtersArray: params.filtersArray } : {}),
    })
    .then((r) => r.data.data);
}

/**
 * The three aggregates behind the CRM dashboard's chart row (lead source
 * categories, monthly lead/conversion trend, interested services). Takes the same
 * filtersArray as the rest of the page.
 */
export function getCRMDashboardCharts(params?: {
  filtersArray?: string;
}): Promise<CRMDashboardChartsResponse> {
  return apiClient
    .get<ApiResponse<CRMDashboardChartsResponse>>('/crm/dashboard-charts', {
      params: params?.filtersArray ? { filtersArray: params.filtersArray } : undefined,
    })
    .then((r) => r.data.data);
}

/**
 * The CRM dashboard's lead performance cards: New Plans by Location, New
 * Patients by Source and New Patient Sign-Ups. Takes the page's filtersArray,
 * plus the New Patients card's own source choice in the same value format as
 * the Lead Source filter (`filtersgroup-<id>` for a whole category).
 */
export function getCRMLeadPerformance(params: {
  filtersArray?: string;
  pickedSources?: string[];
}): Promise<CRMLeadPerformanceResponse> {
  const query: Record<string, string> = {};
  if (params.filtersArray) query.filtersArray = params.filtersArray;
  if (params.pickedSources && params.pickedSources.length > 0) {
    query.pickedSources = params.pickedSources.join(',');
  }

  return apiClient
    .get<ApiResponse<CRMLeadPerformanceResponse>>('/crm/lead-performance', {
      params: Object.keys(query).length > 0 ? query : undefined,
    })
    .then((r) => r.data.data);
}

export function getCRMPipelineStageMap(): Promise<PipelineStageMapResponse> {
  return apiClient
    .get<ApiResponse<PipelineStageMapResponse>>('/crm/pipeline-stage-map')
    .then((r) => r.data.data);
}
